import { useState } from "react";
import { debug } from "@/lib/debug";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronUp, ChevronDown, Trash2, Plus, Search, Ban, Edit, Download, LogIn, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/auth/PasswordInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useApiMutation } from "@/hooks/useApiMutation";


import { format } from "date-fns";
import { usePagination } from "@/hooks/usePagination";
import { Pagination } from "@/components/Pagination";
import { Textarea } from "@/components/ui/textarea";
import { EditUserDialog } from "@/components/admin/users/EditUserDialog";

type User = {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  role: string;
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
  planName?: string;
  subscriptionStatus?: string;
  paypalSubscriptionId?: string;
};

type SortConfig = {
  key: keyof User;
  direction: "asc" | "desc";
} | null;

function getSortedUsers(users: User[], sortConfig: SortConfig): User[] {
  if (!sortConfig) return users;
  
  const sorted = [...users].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortConfig.direction === "asc" 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === "boolean" && typeof bValue === "boolean") {
      return sortConfig.direction === "asc" 
        ? (aValue === bValue ? 0 : aValue ? -1 : 1)
        : (aValue === bValue ? 0 : aValue ? 1 : -1);
    }
    
    return 0;
  });
  
  return sorted;
}

export default function ReportsUsers() {
  
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: "createdAt", direction: "desc" }); // Sort by latest signup first
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ userId: string; email: string } | null>(null);
  const [roleChangeConfirm, setRoleChangeConfirm] = useState<{ userId: string; email: string; newRole: string } | null>(null);
  const [accessChangeConfirm, setAccessChangeConfirm] = useState<{ userId: string; email: string; newAccess: boolean } | null>(null);
  const [newUserData, setNewUserData] = useState({ email: "", password: "", firstName: "", lastName: "" });
  const [createUserErrors, setCreateUserErrors] = useState({ password: "" });

  const validateCreateUserPassword = (password: string) => {
    if (password.length > 0 && password.length < 6) {
      setCreateUserErrors({ password: 'Password must be at least 6 characters' });
    } else if (password.length > 50) {
      setCreateUserErrors({ password: 'Password must be 50 characters or less' });
    } else {
      setCreateUserErrors({ password: '' });
    }
  };
  const [subscriptionDialog, setSubscriptionDialog] = useState<{ userId: string; email: string } | null>(null);
  const [subscriptionData, setSubscriptionData] = useState({ planId: "", amount: "", billingCycle: "monthly", paymentMethod: "", notes: "" });
  const [terminateDialog, setTerminateDialog] = useState<{ userId: string; email: string; paypalSubscriptionId?: string } | null>(null);
  const [terminateData, setTerminateData] = useState({ reason: "", notes: "", cancelPayPal: true });
  const [terminateConfirm, setTerminateConfirm] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);

  // ── Impersonation ──────────────────────────────────────────────────────────
  const [impersonateUser, setImpersonateUser] = useState<User | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("");

  const { data: impersonateWorkspaces = [], isLoading: workspacesLoading } = useQuery<{ _id: string; name: string; type?: string }[]>({
    queryKey: ["impersonate-workspaces", impersonateUser?.id],
    queryFn: async () => {
      const data = await apiRequest('GET', `/api/admin/impersonate/workspaces/${impersonateUser!.id}`);
      return (data as any).workspaces ?? [];
    },
    enabled: !!impersonateUser,
  });

  const impersonateMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/admin/impersonate", {
        userId: impersonateUser!.id,
        workspaceId: selectedWorkspaceId,
      });
    },
    onSuccess: (data) => {
      const mainAppUrl = import.meta.env.VITE_MAIN_APP_URL || window.location.origin;
      const url = `${mainAppUrl}?impersonate_token=${encodeURIComponent((data as any).token)}&workspace_id=${encodeURIComponent(selectedWorkspaceId)}`;
      window.open(url, "_blank");
      setImpersonateUser(null);
      setSelectedWorkspaceId("");
    },
    onError: (err: Error) => {
      showError(err.message ?? "Failed to start impersonation");
    },
  });

  const { data: usersData, isLoading: usersLoading } = useQuery<User[] | { data: User[] }>({
    queryKey: ["/api/admin/users", { search: searchQuery, showDeleted }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      params.append('showDeleted', showDeleted.toString());
      const url = `/api/admin/users?${params.toString()}`;
      const data = await apiRequest('GET', url) as any;
      debug.log('🔍 ReportsUsers queryFn - response data:', data);
      return data;
    }
  });

  const { data: plansData } = useQuery<any>({
    queryKey: ["/api/admin/subscription-plans"],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/subscription-plans');
      return response;
    }
  });

  const plans = Array.isArray(plansData) ? plansData : (plansData?.data || []);

  const users = Array.isArray(usersData) ? usersData : (usersData?.data || []);
  debug.log('🔍 ReportsUsers - usersData:', usersData);
  debug.log('🔍 ReportsUsers - users array:', users);
  debug.log('🔍 ReportsUsers - users length:', users.length);
  const sortedUsers = getSortedUsers(users, sortConfig);
  const deletedCount = users.filter(u => !u.isActive).length;
  const pagination = usePagination(sortedUsers, 10);

  const handleSort = (key: keyof User) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const addUserMutation = useApiMutation({
    endpoint: "/api/admin/users",
    method: "POST",
    invalidate: [["/api/admin/users"]],
    successMessage: (data: { message?: string }) => data?.message || "User created successfully",
    onSuccess: () => {
      setNewUserData({ email: "", password: "", firstName: "", lastName: "" });
      setCreateUserErrors({ password: "" });
      setIsAddDialogOpen(false);
    },
  });

  const updateRoleMutation = useApiMutation({
    endpoint: (vars: { userId: string; role?: string }) => `/api/admin/users/${vars.userId}/role`,
    method: "PATCH",
    invalidate: [["/api/admin/users"]],
    successMessage: "User role updated",
  });

  const toggleAccessMutation = useApiMutation({
    endpoint: (vars: { userId: string; isActive?: boolean }) => `/api/admin/users/${vars.userId}/access`,
    method: "PATCH",
    invalidate: [["/api/admin/users"]],
    successMessage: "User access updated",
  });

  const deleteUserMutation = useApiMutation({
    endpoint: (vars: { userId: string }) => `/api/admin/users/${vars.userId}`,
    method: "DELETE",
    invalidate: [["/api/admin/users"], ["/api/admin/stats"]],
    successMessage: "User access disabled successfully",
    onSuccess: () => {
      setDeleteConfirm(null);
    },
    onError: () => {
      setDeleteConfirm(null);
    },
  });

  const assignSubscriptionMutation = useApiMutation({
    endpoint: (vars: { userId: string; planId?: string; amount?: number | string; billingCycle?: string; paymentMethod?: string; notes?: string }) => `/api/admin/users/${vars.userId}/subscription`,
    method: "POST",
    invalidate: [["/api/admin/users"]],
    successMessage: "Subscription assigned successfully",
    onSuccess: () => {
      setSubscriptionDialog(null);
      setSubscriptionData({ planId: "", amount: "", billingCycle: "monthly", paymentMethod: "", notes: "" });
    },
  });

  const terminateSubscriptionMutation = useApiMutation({
    endpoint: (vars: { paypalSubscriptionId: string; reason?: string; immediateTermination?: boolean; cancelPayPal?: boolean }) => `/api/admin/subscriptions/${vars.paypalSubscriptionId}/cancel`,
    method: "POST",
    invalidate: [["/api/admin/users"]],
    errorMessage: "Subscription terminated immediately",
    onSuccess: () => {
      setTerminateDialog(null);
      setTerminateData({ reason: "", notes: "", cancelPayPal: true });
      setTerminateConfirm(false);
    },
  });

  const handleTerminateSubscription = (data: { paypalSubscriptionId: string; reason: string; notes: string; cancelPayPal: boolean }) => {
    terminateSubscriptionMutation.mutate({
      paypalSubscriptionId: data.paypalSubscriptionId,
      reason: `${data.reason}${data.notes ? `: ${data.notes}` : ''}`,
      immediateTermination: true,
      cancelPayPal: data.cancelPayPal
    });
  };

  const handleDeleteClick = (userId: string, email: string) => {
    setDeleteConfirm({ userId, email });
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteUserMutation.mutate({ userId: deleteConfirm.userId });
    }
  };

  const exportToCSV = () => {
    const headers = ["Email", "Name", "Role", "Status", "Last Login", "Subscription", "Signed Up"];
    const rows = sortedUsers.map(user => [
      user.email,
      user.name || "-",
      user.role,
      user.isActive ? "Active" : "Deleted",
      user.lastLogin ? format(new Date(user.lastLogin), "MMM d, yyyy") : "Never",
      user.planName || "None",
      user.createdAt ? format(new Date(user.createdAt), "MMM d, yyyy") : "N/A"
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `users-export-${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Desktop: Full header */}
      <div className="hidden md:block">
        <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Reports - Users</h1>
        <p className="text-muted-foreground">Enhanced user list with management capabilities</p>
      </div>

      {/* Mobile: Compact header */}
      <div className="md:hidden">
        <h1 className="text-xl font-bold">Users</h1>
      </div>

      <Card>
        {/* Desktop: Header with button */}
        <CardHeader className="hidden md:flex flex-row items-center justify-between gap-2 space-y-0">
          <div>
            <CardTitle>All Users</CardTitle>
            <CardDescription>
              {sortedUsers.length} users found
              {deletedCount > 0 && ` (${deletedCount} deleted)`}
            </CardDescription>
          </div>
          <Button variant="outline" onClick={exportToCSV} className="mr-2">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <PasswordInput
                    id="password"
                    value={newUserData.password}
                    onChange={(e) => {
                      const newPassword = e.target.value;
                      setNewUserData({ ...newUserData, password: newPassword });
                      validateCreateUserPassword(newPassword);
                    }}
                    placeholder="Password (6-50 characters)"
                    className={createUserErrors.password ? "border-red-500" : ""}
                  />
                  {createUserErrors.password && (
                    <p className="text-sm text-red-500 mt-1">{createUserErrors.password}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="firstName">First Name (Optional)</Label>
                    <Input
                      id="firstName"
                      value={newUserData.firstName}
                      onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                      placeholder="First Name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name (Optional)</Label>
                    <Input
                      id="lastName"
                      value={newUserData.lastName}
                      onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                      placeholder="Last Name"
                    />
                  </div>
                </div>
                <Button
                  onClick={() => addUserMutation.mutate(newUserData)}
                  disabled={addUserMutation.isPending || !newUserData.email || !newUserData.password || !!createUserErrors.password}
                  className="w-full"
                >
                  {addUserMutation.isPending ? "Creating..." : "Create User"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>

        {/* Mobile: Add User button */}
        <CardHeader className="md:hidden pt-0">
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportToCSV} className="flex-1">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="flex-1">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email-mobile">Email</Label>
                  <Input
                    id="email-mobile"
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="password-mobile">Password</Label>
                  <PasswordInput
                    id="password-mobile"
                    value={newUserData.password}
                    onChange={(e) => {
                      const newPassword = e.target.value;
                      setNewUserData({ ...newUserData, password: newPassword });
                      validateCreateUserPassword(newPassword);
                    }}
                    placeholder="Password (6-50 characters)"
                    className={createUserErrors.password ? "border-red-500" : ""}
                  />
                  {createUserErrors.password && (
                    <p className="text-sm text-red-500 mt-1">{createUserErrors.password}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="firstName-mobile">First Name (Optional)</Label>
                    <Input
                      id="firstName-mobile"
                      value={newUserData.firstName}
                      onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                      placeholder="First Name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName-mobile">Last Name (Optional)</Label>
                    <Input
                      id="lastName-mobile"
                      value={newUserData.lastName}
                      onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                      placeholder="Last Name"
                    />
                  </div>
                </div>
                <Button
                  onClick={() => addUserMutation.mutate(newUserData)}
                  disabled={addUserMutation.isPending || !newUserData.email || !newUserData.password || !!createUserErrors.password}
                  className="w-full"
                >
                  {addUserMutation.isPending ? "Creating..." : "Create User"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <div className="space-y-3">
            {/* Desktop: Show user count */}
            <div className="hidden md:block text-sm text-muted-foreground">
              {sortedUsers.length} users found
              {deletedCount > 0 && ` (${deletedCount} deleted)`}
            </div>
            
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showDeleted"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="h-4 w-4"
              />
              <label htmlFor="showDeleted" className="text-sm text-muted-foreground cursor-pointer">
                Show deleted users
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead onClick={() => handleSort("email")} className="cursor-pointer hover:opacity-70">
                    <div className="flex items-center gap-2">
                      Email
                      {sortConfig?.key === "email" && (sortConfig.direction === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </div>
                  </TableHead>
                  <TableHead onClick={() => handleSort("name")} className="cursor-pointer hover:opacity-70">
                    <div className="flex items-center gap-2">
                      Name
                      {sortConfig?.key === "name" && (sortConfig.direction === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </div>
                  </TableHead>
                  <TableHead onClick={() => handleSort("role")} className="cursor-pointer hover:opacity-70">
                    <div className="flex items-center gap-2">
                      Role
                      {sortConfig?.key === "role" && (sortConfig.direction === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </div>
                  </TableHead>
                  <TableHead onClick={() => handleSort("isActive")} className="cursor-pointer hover:opacity-70">
                    <div className="flex items-center gap-2">
                      Access
                      {sortConfig?.key === "isActive" && (sortConfig.direction === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </div>
                  </TableHead>
                  <TableHead onClick={() => handleSort("lastLogin")} className="cursor-pointer hover:opacity-70">
                    <div className="flex items-center gap-2">
                      Last Login
                      {sortConfig?.key === "lastLogin" && (sortConfig.direction === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </div>
                  </TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead onClick={() => handleSort("createdAt")} className="cursor-pointer hover:opacity-70">
                    <div className="flex items-center gap-2">
                      Registered
                      {sortConfig?.key === "createdAt" && (sortConfig.direction === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </div>
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.paginatedItems.map((user) => (
                  <TableRow key={user.id} className={!user.isActive ? "opacity-50 bg-red-50 dark:bg-red-950/10" : ""}>
                    <TableCell className="font-medium">
                      {user.email}
                      {!user.isActive && <span className="ml-2 text-xs text-red-600 dark:text-red-400">(Deleted)</span>}
                    </TableCell>
                    <TableCell>{user.name || "-"}</TableCell>
                    <TableCell>
                      <Select value={user.role} onValueChange={(role) => setRoleChangeConfirm({ userId: user.id, email: user.email, newRole: role })}>
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={user.isActive}
                        onCheckedChange={(checked) => setAccessChangeConfirm({ userId: user.id, email: user.email, newAccess: checked })}
                      />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {user.lastLogin ? format(new Date(user.lastLogin), "MMM d, yyyy h:mm a") : "Never"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {user.subscriptionStatus === 'expired' ? (
                        <span className="text-red-600 font-medium">Expired</span>
                      ) : user.subscriptionStatus === 'trial' ? (
                        <span className="text-gray-500">Trial</span>
                      ) : user.planName ? (
                        <span className={user.subscriptionStatus === 'active' ? 'text-green-600' : 'text-gray-500'}>
                          {user.planName}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">None</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {user.createdAt ? format(new Date(user.createdAt), "MMM d, yyyy h:mm a") : "N/A"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setSubscriptionDialog({ userId: user.id, email: user.email })}
                          title="Assign Subscription"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditUser(user)}
                          title="Edit User"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setImpersonateUser(user); setSelectedWorkspaceId(""); }}
                          title="Impersonate User"
                        >
                          <LogIn className="w-4 h-4 text-primary" />
                        </Button>
                        {user.subscriptionStatus === 'active' && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setTerminateDialog({ 
                              userId: user.id, 
                              email: user.email,
                              paypalSubscriptionId: user.paypalSubscriptionId
                            })}
                            title="Terminate Subscription"
                            disabled={!user.paypalSubscriptionId}
                          >
                            <Ban className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteClick(user.id, user.email)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!usersLoading && sortedUsers.length > 0 && (
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.setCurrentPage}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageSizeChange={pagination.setItemsPerPage}
            />
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable User Access?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable access for <strong>{deleteConfirm?.email}</strong>?
              <span className="block mt-2 text-amber-600">The user will not be able to access ChefAiQ. You can re-enable access later using the Access toggle.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleteUserMutation.isPending} className="bg-destructive hover:bg-destructive/90">
              {deleteUserMutation.isPending ? 'Disabling...' : 'Disable Access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!roleChangeConfirm} onOpenChange={() => setRoleChangeConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change User Role?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change <strong>{roleChangeConfirm?.email}</strong>'s role to <strong>{roleChangeConfirm?.newRole}</strong>?
              <span className="block mt-2 text-amber-600">This will change their permissions immediately.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (roleChangeConfirm) {
                  updateRoleMutation.mutate({ userId: roleChangeConfirm.userId, role: roleChangeConfirm.newRole });
                  setRoleChangeConfirm(null);
                }
              }}
              disabled={updateRoleMutation.isPending}
            >
              {updateRoleMutation.isPending ? 'Updating...' : 'Change Role'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!accessChangeConfirm} onOpenChange={() => setAccessChangeConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{accessChangeConfirm?.newAccess ? 'Enable' : 'Disable'} User Access?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {accessChangeConfirm?.newAccess ? 'enable' : 'disable'} access for <strong>{accessChangeConfirm?.email}</strong>?
              <span className="block mt-2 text-amber-600">
                {accessChangeConfirm?.newAccess 
                  ? 'The user will be able to access ChefAiQ again.' 
                  : 'The user will not be able to access ChefAiQ.'}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (accessChangeConfirm) {
                  toggleAccessMutation.mutate({ userId: accessChangeConfirm.userId, isActive: accessChangeConfirm.newAccess });
                  setAccessChangeConfirm(null);
                }
              }}
              disabled={toggleAccessMutation.isPending}
            >
              {toggleAccessMutation.isPending ? 'Updating...' : (accessChangeConfirm?.newAccess ? 'Enable' : 'Disable')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!subscriptionDialog} onOpenChange={() => setSubscriptionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Manual Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>User</Label>
              <Input value={subscriptionDialog?.email || ""} disabled />
            </div>
            <div>
              <Label htmlFor="plan">Subscription Plan</Label>
              <Select value={subscriptionData.planId} onValueChange={(value) => setSubscriptionData({ ...subscriptionData, planId: value })}>
                <SelectTrigger id="plan">
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.filter((p: any) => p.isActive).map((plan: any) => (
                    <SelectItem key={plan._id} value={plan._id}>
                      {plan.name} - ₱{plan.price}/{plan.billingCycle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="billingCycle">Billing Cycle</Label>
              <Select value={subscriptionData.billingCycle} onValueChange={(value) => setSubscriptionData({ ...subscriptionData, billingCycle: value })}>
                <SelectTrigger id="billingCycle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="amount">Amount Paid (₱)</Label>
              <Input
                id="amount"
                type="number"
                value={subscriptionData.amount}
                onChange={(e) => setSubscriptionData({ ...subscriptionData, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="paymentMethod">Payment Method</Label>
              <Select value={subscriptionData.paymentMethod} onValueChange={(value) => setSubscriptionData({ ...subscriptionData, paymentMethod: value })}>
                <SelectTrigger id="paymentMethod">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GCash">GCash</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={subscriptionData.notes}
                onChange={(e) => setSubscriptionData({ ...subscriptionData, notes: e.target.value })}
                placeholder="Add any additional notes..."
                rows={3}
              />
            </div>
            <Button 
              onClick={() => {
                if (subscriptionDialog) {
                  assignSubscriptionMutation.mutate({
                    userId: subscriptionDialog.userId,
                    planId: subscriptionData.planId,
                    amount: subscriptionData.amount,
                    billingCycle: subscriptionData.billingCycle,
                    paymentMethod: subscriptionData.paymentMethod,
                    notes: subscriptionData.notes
                  });
                }
              }}
              disabled={assignSubscriptionMutation.isPending || !subscriptionData.planId || !subscriptionData.amount || !subscriptionData.paymentMethod}
              className="w-full"
            >
              {assignSubscriptionMutation.isPending ? "Assigning..." : "Assign Subscription"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!terminateDialog} onOpenChange={() => {
        setTerminateDialog(null);
        setTerminateData({ reason: "", notes: "", cancelPayPal: true });
        setTerminateConfirm(false);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Terminate Subscription Immediately</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>User</Label>
              <Input value={terminateDialog?.email || ""} disabled />
            </div>
            
            <div className="space-y-3 border-t pt-3">
              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="cancelPayPal" 
                  checked={terminateData.cancelPayPal}
                  onCheckedChange={(checked) => setTerminateData({ ...terminateData, cancelPayPal: checked as boolean })}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="cancelPayPal"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Cancel PayPal subscription
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Cancel the subscription on PayPal (recommended). If unchecked, only local access will be revoked.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="reason">Termination Reason *</Label>
              <Select value={terminateData.reason} onValueChange={(value) => setTerminateData({ ...terminateData, reason: value })}>
                <SelectTrigger id="reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="terms_violation">Terms Violation</SelectItem>
                  <SelectItem value="refund_request">Refund Request</SelectItem>
                  <SelectItem value="fraud">Fraud</SelectItem>
                  <SelectItem value="chargeback">Chargeback</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={terminateData.notes}
                onChange={(e) => setTerminateData({ ...terminateData, notes: e.target.value })}
                placeholder="Additional details about the termination..."
                rows={3}
              />
            </div>
            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <p className="text-sm text-red-800 dark:text-red-200 font-medium">⚠️ Warning</p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                This will immediately terminate the subscription. The user will lose access instantly. This action is logged for audit purposes.
              </p>
            </div>
            <Button 
              onClick={() => {
                if (!terminateConfirm) {
                  setTerminateConfirm(true);
                } else {
                  if (terminateDialog?.paypalSubscriptionId) {
                    handleTerminateSubscription({
                      paypalSubscriptionId: terminateDialog.paypalSubscriptionId,
                      reason: terminateData.reason,
                      notes: terminateData.notes,
                      cancelPayPal: terminateData.cancelPayPal
                    });
                  }
                }
              }}
              disabled={terminateSubscriptionMutation.isPending || !terminateData.reason || !terminateDialog?.paypalSubscriptionId}
              variant={terminateConfirm ? "destructive" : "default"}
              className="w-full"
            >
              {terminateSubscriptionMutation.isPending 
                ? "Terminating..." 
                : terminateConfirm 
                  ? "⚠️ Click Again to Confirm Termination" 
                  : "Terminate Subscription"}
            </Button>
            {terminateConfirm && (
              <Button 
                onClick={() => setTerminateConfirm(false)}
                variant="outline"
                className="w-full"
              >
                Cancel
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <EditUserDialog
        open={!!editUser}
        onOpenChange={(open) => !open && setEditUser(null)}
        user={editUser}
      />

      {/* ── Impersonate: workspace picker ── */}
      <Dialog
        open={!!impersonateUser}
        onOpenChange={(open) => { if (!open) { setImpersonateUser(null); setSelectedWorkspaceId(""); } }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5 text-primary" />
              Impersonate User
            </DialogTitle>
            <DialogDescription>
              Select a workspace to open the app as{" "}
              <strong>
                {impersonateUser?.firstName
                  ? `${impersonateUser.firstName} ${impersonateUser.lastName ?? ""}`.trim()
                  : impersonateUser?.email}
              </strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {workspacesLoading && (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="h-12 rounded-md bg-muted animate-pulse" />
                ))}
              </div>
            )}

            {!workspacesLoading && impersonateWorkspaces.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No workspaces found for this user
              </p>
            )}

            {impersonateWorkspaces.map((ws) => (
              <button
                key={ws._id}
                type="button"
                onClick={() => setSelectedWorkspaceId(ws._id)}
                className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
                  selectedWorkspaceId === ws._id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{ws.name}</div>
                  {ws.type && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1 capitalize mt-0.5">
                      {ws.type}
                    </Badge>
                  )}
                </div>
                {selectedWorkspaceId === ws._id && (
                  <div className="h-2 w-2 rounded-full bg-primary shrink-0" />
                )}
              </button>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonateUser(null)}>
              Cancel
            </Button>
            <Button
              disabled={!selectedWorkspaceId || impersonateMutation.isPending}
              onClick={() => impersonateMutation.mutate()}
              className="gap-2"
            >
              <LogIn className="h-4 w-4" />
              {impersonateMutation.isPending ? "Opening…" : "Start Impersonation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
